/**
 * 日付ユーティリティ。保存は UTC、表示はテナントの TZ（02 §11 #4）。
 * ここでは「暦日（YYYY-MM-DD）」の計算だけを扱う。時刻を伴う値は常に Date（UTC）で持つ。
 */

export type IsoDate = string; // 'YYYY-MM-DD'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(v: unknown): v is IsoDate {
  return typeof v === 'string' && ISO_DATE.test(v) && !Number.isNaN(Date.parse(v + 'T00:00:00Z'));
}

export function toIsoDate(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

export function parseIsoDate(s: IsoDate): Date {
  if (!isIsoDate(s)) throw new Error(`invalid date: ${s}`);
  return new Date(s + 'T00:00:00Z');
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = parseIsoDate(date);
  d.setUTCDate(d.getUTCDate() + days);
  return toIsoDate(d);
}

export function addMonths(date: IsoDate, months: number): IsoDate {
  const d = parseIsoDate(date);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return toIsoDate(d);
}

/**
 * 日本の祝日（内閣府の「国民の祝日」）と名称。振替休日・国民の休日を含む。
 * 2026〜2030 年分を静的に持つ（SFA 1A で 2028 → 2030 に延長）。年をまたぐ運用では毎年 1 月に翌々年分を足すこと。
 *
 * 作り方: 固定日（元日・建国記念の日・天皇誕生日・昭和の日・憲法記念日・みどりの日・こどもの日・山の日・文化の日・勤労感謝の日）
 * + ハッピーマンデー（成人の日 1 月第 2 月曜・海の日 7 月第 3 月曜・敬老の日 9 月第 3 月曜・スポーツの日 10 月第 2 月曜）
 * + 春分の日（3 月 `floor(20.8431 + 0.242194 × (年 − 1980) − floor((年 − 1980) / 4))` 日）・秋分の日（9 月・定数 23.2488）
 * + 振替休日（祝日が日曜 → 次の祝日でない日）+ 国民の休日（祝日に挟まれた平日）。
 * 規則との突き合わせは modules/core/internal/business-calendar/japan-holidays.unit.spec.ts。
 * ★ 春分・秋分は前年 2 月の官報（暦要項）で確定する。2028 年以降は計算式による予測なので、官報が出たら確かめること。
 */
export const JP_HOLIDAY_NAMES: Readonly<Record<IsoDate, string>> = Object.freeze({
  // 2026
  '2026-01-01': '元日',
  '2026-01-12': '成人の日',
  '2026-02-11': '建国記念の日',
  '2026-02-23': '天皇誕生日',
  '2026-03-20': '春分の日',
  '2026-04-29': '昭和の日',
  '2026-05-03': '憲法記念日',
  '2026-05-04': 'みどりの日',
  '2026-05-05': 'こどもの日',
  '2026-05-06': '振替休日',
  '2026-07-20': '海の日',
  '2026-08-11': '山の日',
  '2026-09-21': '敬老の日',
  '2026-09-22': '国民の休日',
  '2026-09-23': '秋分の日',
  '2026-10-12': 'スポーツの日',
  '2026-11-03': '文化の日',
  '2026-11-23': '勤労感謝の日',
  // 2027
  '2027-01-01': '元日',
  '2027-01-11': '成人の日',
  '2027-02-11': '建国記念の日',
  '2027-02-23': '天皇誕生日',
  '2027-03-21': '春分の日',
  '2027-03-22': '振替休日',
  '2027-04-29': '昭和の日',
  '2027-05-03': '憲法記念日',
  '2027-05-04': 'みどりの日',
  '2027-05-05': 'こどもの日',
  '2027-07-19': '海の日',
  '2027-08-11': '山の日',
  '2027-09-20': '敬老の日',
  '2027-09-23': '秋分の日',
  '2027-10-11': 'スポーツの日',
  '2027-11-03': '文化の日',
  '2027-11-23': '勤労感謝の日',
  // 2028
  '2028-01-01': '元日',
  '2028-01-10': '成人の日',
  '2028-02-11': '建国記念の日',
  '2028-02-23': '天皇誕生日',
  '2028-03-20': '春分の日',
  '2028-04-29': '昭和の日',
  '2028-05-03': '憲法記念日',
  '2028-05-04': 'みどりの日',
  '2028-05-05': 'こどもの日',
  '2028-07-17': '海の日',
  '2028-08-11': '山の日',
  '2028-09-18': '敬老の日',
  '2028-09-22': '秋分の日',
  '2028-10-09': 'スポーツの日',
  '2028-11-03': '文化の日',
  '2028-11-23': '勤労感謝の日',
  // 2029
  '2029-01-01': '元日',
  '2029-01-08': '成人の日',
  '2029-02-11': '建国記念の日',
  '2029-02-12': '振替休日',
  '2029-02-23': '天皇誕生日',
  '2029-03-20': '春分の日',
  '2029-04-29': '昭和の日',
  '2029-04-30': '振替休日',
  '2029-05-03': '憲法記念日',
  '2029-05-04': 'みどりの日',
  '2029-05-05': 'こどもの日',
  '2029-07-16': '海の日',
  '2029-08-11': '山の日',
  '2029-09-17': '敬老の日',
  '2029-09-23': '秋分の日',
  '2029-09-24': '振替休日',
  '2029-10-08': 'スポーツの日',
  '2029-11-03': '文化の日',
  '2029-11-23': '勤労感謝の日',
  // 2030
  '2030-01-01': '元日',
  '2030-01-14': '成人の日',
  '2030-02-11': '建国記念の日',
  '2030-02-23': '天皇誕生日',
  '2030-03-20': '春分の日',
  '2030-04-29': '昭和の日',
  '2030-05-03': '憲法記念日',
  '2030-05-04': 'みどりの日',
  '2030-05-05': 'こどもの日',
  '2030-05-06': '振替休日',
  '2030-07-15': '海の日',
  '2030-08-11': '山の日',
  '2030-08-12': '振替休日',
  '2030-09-16': '敬老の日',
  '2030-09-23': '秋分の日',
  '2030-10-14': 'スポーツの日',
  '2030-11-03': '文化の日',
  '2030-11-04': '振替休日',
  '2030-11-23': '勤労感謝の日',
});

/** 同梱の祝日がある年の範囲（両端を含む） */
export const JP_HOLIDAY_YEARS: Readonly<{ from: number; to: number }> = Object.freeze({ from: 2026, to: 2030 });

/** 日本の祝日の日付だけの集合（JP_HOLIDAY_NAMES のキー） */
export const JP_HOLIDAYS: ReadonlySet<IsoDate> = new Set<IsoDate>(Object.keys(JP_HOLIDAY_NAMES));

export function isBusinessDay(date: IsoDate, holidays: ReadonlySet<IsoDate> = JP_HOLIDAYS): boolean {
  const dow = parseIsoDate(date).getUTCDay();
  if (dow === 0 || dow === 6) return false;
  return !holidays.has(date);
}

/** 営業日を n 日進める（当日は数えない）。 */
export function addBusinessDays(date: IsoDate, n: number, holidays: ReadonlySet<IsoDate> = JP_HOLIDAYS): IsoDate {
  let cur = date;
  let remaining = n;
  while (remaining > 0) {
    cur = addDays(cur, 1);
    if (isBusinessDay(cur, holidays)) remaining -= 1;
  }
  return cur;
}

export function todayIso(now: Date = new Date()): IsoDate {
  return toIsoDate(now);
}

export function diffDays(from: IsoDate, to: IsoDate): number {
  return Math.round((parseIsoDate(to).getTime() - parseIsoDate(from).getTime()) / 86_400_000);
}
